import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import { PushNotifications } from '@capacitor/push-notifications';

/**
 * Hook para manejar deeplinks cuando el usuario hace click en notificaciones push
 * Solo activo en plataformas nativas (Android/iOS)
 */
export const usePushNotificationDeeplinks = () => {
  const navigate = useNavigate();
  const isNative = Capacitor.isNativePlatform();

  useEffect(() => {
    if (!isNative) return;

    let removed = false;
    let cleanup: (() => void) | null = null;

    const handleAction = (data: any) => {
      if (removed) return;
      console.log('📱 Push notification clicked:', data);

      const link = data?.link;
      const callId = data?.callId;
      const type = String(data?.type || '');
      const eventId = String(data?.eventId || data?.resourceId || '');

      if (callId) {
        console.log('📞 Deep link: Navegando a llamada entrante', callId);
        navigate(`/calls/incoming/${callId}`);
        return;
      }

      const computeTarget = () => {
        if (link) return link;
        if (type === 'schedule_start' || type === 'daily_summary' || type === 'weekly_summary' || type === 'task_reminder') {
          return '/groups/home-groups';
        }
        if (type === 'system_message' && eventId) {
          return `/groups/calendar?eventId=${eventId}`;
        }
        return '/notifications';
      };

      const target = computeTarget();
      const targetPath = target.startsWith('/') ? target : `/${target}`;
      navigate(targetPath);
    };

    (async () => {
      // Prefer iOS FirebaseMessaging (FCM) if present
      if (Capacitor.getPlatform() === 'ios') {
        try {
          const { FirebaseMessaging } = await import('@capacitor-firebase/messaging');
          const listener = await FirebaseMessaging.addListener('notificationActionPerformed', (event: any) => {
            const data: any = event?.notification?.data || event?.notification?.notification?.data || event?.data || {};
            handleAction(data);
          });
          cleanup = () => {
            try { listener?.remove?.(); } catch {}
          };
          return;
        } catch {
          // fallback below
        }
      }

      // Default: Capacitor PushNotifications
      const actionListener = await PushNotifications.addListener('pushNotificationActionPerformed', (notification) => {
        const data: any = notification.notification?.data || {};
        handleAction(data);
      });
      cleanup = () => {
        try { actionListener.remove(); } catch {}
      };
    })();

    return () => {
      removed = true;
      try {
        cleanup?.();
      } catch {
      }
    };
  }, [navigate, isNative]);
};
